import { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { calculateAll, type CipherResult } from '@gematria/shared'
import { ExportButton, ImportButton } from './ExportButton'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  useDroppable,
} from '@dnd-kit/core'

const API_URL = import.meta.env.PROD ? '' : 'http://localhost:3001'

// Cipher colors matching the main calculator
const cipherColors: Record<string, { name: string; value: string; bg: string }> = {
  'Alphanumeric Qabbala': { name: 'text-stone-300', value: 'text-stone-100', bg: 'bg-stone-900' },
  'AQ Squares': { name: 'text-[#B89B6B]', value: 'text-[#D4B896]', bg: 'bg-[#2A2015]' },
  'AQ Primes': { name: 'text-[#D4B896]', value: 'text-[#EBD4B0]', bg: 'bg-[#352A1F]' },
  'AQ Trigonal': { name: 'text-[#EBD4B0]', value: 'text-[#F5E6D0]', bg: 'bg-[#403526]' },
  'Ordinal': { name: 'text-stone-200', value: 'text-stone-100', bg: 'bg-stone-700' },
  'Numeric QWERTY': { name: 'text-green-400', value: 'text-green-200', bg: 'bg-green-950' },
  'NQ Squares': { name: 'text-[#5A9B5A]', value: 'text-[#7ABD7A]', bg: 'bg-[#0F2510]' },
  'NQ Primes': { name: 'text-[#6ABD6A]', value: 'text-[#8BDF8B]', bg: 'bg-[#1A351A]' },
  'NQ Trigonal': { name: 'text-[#8BDF8B]', value: 'text-[#ABFFAB]', bg: 'bg-[#2A452A]' },
  'QWERTY': { name: 'text-lime-400', value: 'text-lime-200', bg: 'bg-lime-950' },
  'Standard': { name: 'text-[#E8A830]', value: 'text-[#F8C040]', bg: 'bg-[#3D2815]' },
  'Satanic': { name: 'text-red-500', value: 'text-red-300', bg: 'bg-red-950' },
  'Synx': { name: 'text-cyan-400', value: 'text-cyan-200', bg: 'bg-cyan-950' },
  'Reduction': { name: 'text-[#F0B840]', value: 'text-[#F8D060]', bg: 'bg-[#4A3220]' },
  'Vaxen': { name: 'text-violet-400', value: 'text-violet-200', bg: 'bg-violet-950' },
  'Unun': { name: 'text-indigo-400', value: 'text-indigo-200', bg: 'bg-indigo-950' },
  'Based Atlanteanism': { name: 'text-amber-500', value: 'text-amber-300', bg: 'bg-amber-950' },
}

// Cipher order matching the calculator page
const cipherOrder = [
  // Alphanumeric line
  'Alphanumeric Qabbala',
  'Ordinal',
  'AQ Primes',
  'AQ Squares',
  'AQ Trigonal',
  // QWERTY line
  'Numeric QWERTY',
  'QWERTY',
  'NQ Primes',
  'NQ Squares',
  'NQ Trigonal',
  // Other line
  'Synx',
  'Unun',
  'Vaxen',
  'Based Atlanteanism',
  'Satanic',
  'Standard',
  'Reduction',
]

const cipherDisplayNames: Record<string, string> = {
  'Alphanumeric Qabbala': 'AQ',
  'Numeric QWERTY': 'NQ',
}

const tripleNumbers = [111, 222, 333, 444, 555, 666, 777, 888, 999, 1111, 2222, 3333, 4444, 5555, 6666, 7777, 8888, 9999]

interface User {
  id: string
  username: string
}

interface StoredPhrase {
  phrase_hash: string
  phrase: string
  results: CipherResult[]
  timestamp: number | string
  category?: string
  tags?: string[] | string  // Can be array or comma-separated string from backend
  discoveredBy?: string | null
  like_count: number
  userLiked: boolean
}

interface ProfilePageProps {
  user: User | null
  userCategories: string[]
  setUserCategories: React.Dispatch<React.SetStateAction<string[]>>
}

// Separate component for droppable folder to fix hooks error
interface FolderItemProps {
  category: string
  phrasesInCategory: StoredPhrase[]
  filteredCount: number
  isExpanded: boolean
  isDraggingPhrases: boolean
  activeDropTarget: string | null
  setActiveDropTarget: (target: string | null) => void
  toggleCategory: (category: string) => void
  setFolderToDelete: (folder: string | null) => void
  searchFilter: string
  filterPhrase: (phrase: StoredPhrase) => boolean
  setEditingPhrase: (phrase: StoredPhrase | null) => void
  setEditTags: (tags: string) => void
  setEditCategory: (category: string) => void
  normalizeTags: (tags: string[] | string | undefined) => string[]
}

function FolderItem({
  category,
  phrasesInCategory,
  filteredCount,
  isExpanded,
  isDraggingPhrases,
  activeDropTarget,
  setActiveDropTarget,
  toggleCategory,
  setFolderToDelete,
  searchFilter,
  filterPhrase,
  setEditingPhrase,
  setEditTags,
  setEditCategory,
  normalizeTags
}: FolderItemProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: category,
    data: { category },
    disabled: !isDraggingPhrases
  })

  // Track active drop target
  useEffect(() => {
    if (isOver && isDraggingPhrases && activeDropTarget !== category) {
      setActiveDropTarget(category)
    } else if (!isOver && activeDropTarget === category) {
      setActiveDropTarget(null)
    }
  }, [isOver, isDraggingPhrases, activeDropTarget, category, setActiveDropTarget])

  return (
    <div 
      ref={setNodeRef}
      className={`border rounded-lg overflow-hidden transition-colors ${
        isOver && isDraggingPhrases 
          ? 'border-green-400 bg-green-900/50 ring-2 ring-green-400' 
          : 'border-green-800'
      }`}
    >
      {/* Category Header */}
      <div
        className="w-full flex items-center justify-between px-3 py-2 bg-green-900/30 hover:bg-green-900/50 transition-colors text-left cursor-pointer"
        onClick={() => toggleCategory(category)}
      >
        <div className="flex items-center gap-2">
          <span className="text-green-400">{isExpanded ? '📂' : '📁'}</span>
          <span className="text-green-300 text-sm font-medium">{category} ({phrasesInCategory.length})</span>
        </div>
        <div className="flex items-center gap-1">
          {category !== 'uncategorized' && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setFolderToDelete(category)
              }}
              className="text-red-500 hover:text-red-400 text-xs px-1"
              title="Delete folder"
            >
              ×
            </button>
          )}
        </div>
      </div>
      
      {/* Expanded Phrases */}
      {isExpanded && (
        <div className="bg-black/50">
          {filteredCount === 0 ? (
            <p className="text-green-700 text-xs px-3 py-2 italic">
              {searchFilter ? 'No matching phrases' : 'Empty folder'}
            </p>
          ) : (
            phrasesInCategory
              .filter(filterPhrase)
              .map(phrase => (
                <div
                  key={phrase.timestamp}
                  className="px-3 py-2 border-t border-green-900/50 hover:bg-green-900/20 cursor-pointer"
                  onClick={() => {
                    setEditingPhrase(phrase)
                    const tags = normalizeTags(phrase.tags)
                    setEditTags(tags.join(', '))
                    setEditCategory(phrase.category || 'uncategorized')
                  }}
                >
                  <p className="text-green-400 text-sm truncate">{phrase.phrase}</p>
                </div>
              ))
          )}
        </div>
      )}
    </div>
  )
}

export function ProfilePage({ 
  user, 
  userCategories,
  setUserCategories 
}: ProfilePageProps) {
  const navigate = useNavigate()
  const [searchFilter, setSearchFilter] = useState('')
  const [searchByValue, setSearchByValue] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [editingPhrase, setEditingPhrase] = useState<StoredPhrase | null>(null)
  const [editTags, setEditTags] = useState('')
  const [editCategory, setEditCategory] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [visibleCiphers, setVisibleCiphers] = useState<Set<string>>(() => {
    // Try to load from localStorage first
    const saved = localStorage.getItem('profileVisibleCiphers')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) {
          return new Set(parsed)
        }
      } catch {
        return new Set()
      }
    }
    return new Set()
  })
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null)
  const [discovererError, setDiscovererError] = useState<string | null>(null)
  const [profilePhrases, setProfilePhrases] = useState<StoredPhrase[]>([])

  // Selection state for bulk drag-to-folder
  const [selectedPhrases, setSelectedPhrases] = useState<Set<string>>(new Set())
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectionBox, setSelectionBox] = useState<{ startX: number; startY: number; currentX: number; currentY: number } | null>(null)
  const [isDraggingPhrases, setIsDraggingPhrases] = useState(false)
  const [activeDropTarget, setActiveDropTarget] = useState<string | null>(null)
  const phrasesGridRef = useRef<HTMLDivElement>(null)
  const mainContainerRef = useRef<HTMLDivElement>(null)

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [targetFolder, setTargetFolder] = useState<string>('')
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false)

  // Highlight settings from localStorage (sync with Calculator)
  const [highlightedPhrases] = useState<string[]>(() => {
    try { const s = localStorage.getItem('highlightedPhrases'); return s ? JSON.parse(s) : [] } catch { return [] }
  })
  const [userHighlightedValues, setUserHighlightedValues] = useState<number[]>(() => {
    try { const s = localStorage.getItem('userHighlightedValues'); return s ? JSON.parse(s) : [] } catch { return [] }
  })
  const [disabledDefaultValues, setDisabledDefaultValues] = useState<number[]>(() => {
    try { const s = localStorage.getItem('disabledDefaultValues'); return s ? JSON.parse(s) : [] } catch { return [] }
  })

  const shouldHighlightValue = (value: number): boolean => {
    if (userHighlightedValues.includes(value)) return true
    if (tripleNumbers.includes(value) && !disabledDefaultValues.includes(value)) return true
    return false
  }

  // Listen for highlight changes from other tabs
  useEffect(() => {
    const handler = () => {
      try {
        const s = localStorage.getItem('userHighlightedValues')
        if (s) setUserHighlightedValues(JSON.parse(s))
        const d = localStorage.getItem('disabledDefaultValues')
        if (d) setDisabledDefaultValues(JSON.parse(d))
      } catch {}
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  }, [])

  // Load saved cipher selections from server
  useEffect(() => {
    if (user) {
      loadSavedCipherSelection()
    }
  }, [user])

  const loadSavedCipherSelection = async () => {
    const session = localStorage.getItem('session')
    if (!session) return
    
    try {
      const response = await fetch(`${API_URL}/api/settings/visibleCiphers`, {
        headers: { 'Authorization': `Bearer ${session}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.setting_value) {
          const savedCiphers = JSON.parse(data.setting_value)
          if (Array.isArray(savedCiphers)) {
            setVisibleCiphers(new Set(savedCiphers))
          }
        }
      }
    } catch (error) {
      console.error('Failed to load saved cipher selection:', error)
    }
  }

  const fetchCategories = async () => {
    const session = localStorage.getItem('session')
    if (!session || !user) return
    
    try {
      const response = await fetch(`${API_URL}/api/categories`, {
        headers: { 'Authorization': `Bearer ${session}` }
      })
      if (response.ok) {
        const data = await response.json()
        // Merge with uncategorized
        const allCategories = ['uncategorized', ...data.filter((c: string) => c !== 'uncategorized')]
        setUserCategories(allCategories)
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error)
    }
  }

  // DnD sensors for drag-to-folder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  )

  // Bulk update phrases (drag-to-folder)
  const bulkUpdatePhrases = async (phraseIds: string[], category: string) => {
    const session = localStorage.getItem('session')
    if (!session) {
      console.log('No session found')
      return
    }

    console.log('bulkUpdatePhrases sending:', { phraseIds, category })
    try {
      const csrfToken = localStorage.getItem('csrfToken')
      const response = await fetch(`${API_URL}/api/phrases/bulk-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session}`,
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({ phraseIds, category })
      })

      console.log('Bulk update response status:', response.status)
      if (response.ok) {
        const data = await response.json()
        console.log('Bulk update response:', data)
        // Update local state
        setProfilePhrases(prev => prev.map(p => {
          if (phraseIds.includes(String(p.timestamp))) {
            return { ...p, category }
          }
          return p
        }))
        // Clear selection
        setSelectedPhrases(new Set())
        return data
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('Bulk update failed:', response.status, errorData)
      }
    } catch (error) {
      console.error('Error in bulk update:', error)
    }
  }

  // Selection box handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    // Start selection from anywhere in the main container area (wider range)
    // Only exclude clicking on specific interactive elements
    const target = e.target as HTMLElement
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'A' || 
        target.closest('button') || target.closest('input') || target.closest('a') ||
        target.closest('[data-phrase-id]')) {
      return
    }
    
    // Don't start selection if holding modifier keys (allow Cmd+Click for other actions)
    if (e.metaKey || e.ctrlKey) return
    
    // Prevent text selection
    e.preventDefault()
    
    const containerRect = mainContainerRef.current?.getBoundingClientRect()
    const gridRect = phrasesGridRef.current?.getBoundingClientRect()
    if (!containerRect || !gridRect) return

    // Calculate position relative to the grid
    const x = e.clientX - gridRect.left
    const y = e.clientY - gridRect.top

    setIsSelecting(true)
    setSelectionBox({ startX: x, startY: y, currentX: x, currentY: y })
    // Clear previous selection if not holding Shift
    if (!e.shiftKey) {
      setSelectedPhrases(new Set())
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting || !selectionBox || !phrasesGridRef.current) return

    // Prevent text selection during drag
    e.preventDefault()

    const rect = phrasesGridRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setSelectionBox(prev => prev ? { ...prev, currentX: x, currentY: y } : null)

    // Find all phrase cards that intersect with selection box
    const phraseCards = phrasesGridRef.current.querySelectorAll('[data-phrase-id]')
    const newSelection = new Set<string>(e.shiftKey ? selectedPhrases : [])

    phraseCards.forEach((card) => {
      const cardRect = card.getBoundingClientRect()
      const boxLeft = Math.min(selectionBox.startX, selectionBox.currentX) + rect.left
      const boxRight = Math.max(selectionBox.startX, selectionBox.currentX) + rect.left
      const boxTop = Math.min(selectionBox.startY, selectionBox.currentY) + rect.top
      const boxBottom = Math.max(selectionBox.startY, selectionBox.currentY) + rect.top

      const intersects = !(
        cardRect.right < boxLeft ||
        cardRect.left > boxRight ||
        cardRect.bottom < boxTop ||
        cardRect.top > boxBottom
      )

      if (intersects) {
        const phraseId = (card as HTMLElement).dataset.phraseId
        if (phraseId) newSelection.add(phraseId)
      }
    })

    setSelectedPhrases(newSelection)
  }

  const handleMouseUp = () => {
    setIsSelecting(false)
    setSelectionBox(null)
  }

  // Handle drag end (drop on folder)
  const handleDragEnd = (event: DragEndEvent) => {
    const { over } = event
    setIsDraggingPhrases(false)
    setActiveDropTarget(null)

    if (over && selectedPhrases.size > 0) {
      const targetCategory = over.id as string
      const phraseIds = Array.from(selectedPhrases)
      bulkUpdatePhrases(phraseIds, targetCategory)
    }
  }

  // Right-click context menu handler
  const handleContextMenu = (e: React.MouseEvent, phrase: StoredPhrase) => {
    e.preventDefault()
    
    // Only show context menu if this phrase is selected or if there are selected phrases
    const phraseId = String(phrase.timestamp)
    const hasSelection = selectedPhrases.size > 0
    const isSelected = selectedPhrases.has(phraseId)
    
    if (!hasSelection) {
      // If right-clicking without any selection, select just this phrase
      setSelectedPhrases(new Set([phraseId]))
    } else if (!isSelected) {
      // If right-clicking a non-selected phrase while others are selected, select just this one
      setSelectedPhrases(new Set([phraseId]))
    }
    
    // Show context menu
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      visible: true
    })
  }

  // Close context menu on click elsewhere
  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, visible: false }))
  }

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      // Don't close if clicking inside the context menu or delete confirmation
      if (target.closest('[data-context-menu]') || target.closest('[data-delete-confirm]')) {
        return
      }
      if (contextMenu.visible) {
        closeContextMenu()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [contextMenu.visible])

  // Bulk delete phrases
  const bulkDeletePhrases = async () => {
    if (selectedPhrases.size === 0) return
    
    const session = localStorage.getItem('session')
    if (!session) return

    const phraseIds = Array.from(selectedPhrases)
    let deletedCount = 0
    const csrfToken = localStorage.getItem('csrfToken')
    
    try {
      for (const phraseId of phraseIds) {
        const response = await fetch(`${API_URL}/api/phrases/${phraseId}`, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${session}`,
            'X-CSRF-Token': csrfToken || ''
          }
        })
        if (response.ok) {
          deletedCount++
        }
      }
      
      // Update local state
      setProfilePhrases(prev => prev.filter(p => !selectedPhrases.has(String(p.timestamp))))
      setSelectedPhrases(new Set())
      setShowDeleteConfirm(false)
      setContextMenu(prev => ({ ...prev, visible: false }))
    } catch (error) {
      console.error('Error deleting phrases:', error)
    }
  }

  // Add selected phrases to folder
  const addSelectedToFolder = async () => {
    console.log('addSelectedToFolder called', { selectedPhrases: Array.from(selectedPhrases), targetFolder })
    if (selectedPhrases.size === 0 || !targetFolder) {
      console.log('Early return - no selection or no folder')
      return
    }
    
    const phraseIds = Array.from(selectedPhrases)
    console.log('Calling bulkUpdatePhrases with:', phraseIds, targetFolder)
    await bulkUpdatePhrases(phraseIds, targetFolder)
    
    setTargetFolder('')
    setContextMenu(prev => ({ ...prev, visible: false }))
  }

  // Check discoverer profile
  const checkDiscovererProfile = async (username: string) => {
    if (username === user?.username) {
      // Current user - navigate to their own profile
      navigate('/profile')
      return
    }
    
    try {
      const response = await fetch(`${API_URL}/api/users/${username}`)
      if (response.ok) {
        // Profile exists and is public - navigate to it
        navigate(`/u/${username}`)
      } else if (response.status === 403) {
        setDiscovererError(`${username} has not made their profile public`)
        setTimeout(() => setDiscovererError(null), 3000)
      } else if (response.status === 404) {
        setDiscovererError(`User ${username} not found`)
        setTimeout(() => setDiscovererError(null), 3000)
      } else {
        setDiscovererError('Unable to view this profile')
        setTimeout(() => setDiscovererError(null), 3000)
      }
    } catch (error) {
      setDiscovererError('Unable to view this profile')
      setTimeout(() => setDiscovererError(null), 3000)
    }
  }

  const toggleLike = async (phraseHash: string) => {
    const session = localStorage.getItem('session')
    const csrfToken = localStorage.getItem('csrfToken')
    if (!session || !csrfToken) return
    try {
      const response = await fetch(`${API_URL}/api/discoveries/${phraseHash}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session}`,
          'X-CSRF-Token': csrfToken,
        },
      })
      if (!response.ok) throw new Error('Failed to toggle like')
      const data = await response.json()
      setProfilePhrases(prev => prev.map(p =>
        p.phrase_hash === phraseHash
          ? { ...p, like_count: data.like_count, userLiked: data.liked }
          : p
      ))
    } catch (err) {
      console.error('Error toggling like:', err)
    }
  }

  // Save visibleCiphers to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('profileVisibleCiphers', JSON.stringify([...visibleCiphers]))
  }, [visibleCiphers])

  // Helper function to normalize tags to array - defined at top level of component
  function normalizeTags(tags: string[] | string | undefined): string[] {
    if (!tags) return []
    if (Array.isArray(tags)) return tags
    if (typeof tags === 'string') return tags.split(',').map(t => t.trim()).filter(t => t)
    return []
  }

  useEffect(() => {
    if (user) {
      // Always fetch from server when user logs in or returns to profile
      fetchUserPhrases()
      fetchCategories()
    } else {
      setIsLoading(false)
    }
    // Reset search states when profile page loads
    setSearchFilter('')
  }, [user])

  // Fetch phrases on mount (when navigating to this page)
  useEffect(() => {
    if (user) {
      fetchUserPhrases()
    }
  }, [])

  // Group phrases by category
  const phrasesByCategory = useMemo(() => {
    const grouped: Record<string, StoredPhrase[]> = {}
    
    // Initialize with all categories (even empty ones)
    userCategories.forEach(cat => {
      if (cat !== 'all') {
        grouped[cat] = []
      }
    })
    
    // Group phrases
    profilePhrases.forEach(phrase => {
      const category = phrase.category || 'uncategorized'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(phrase)
    })
    
    return grouped
  }, [profilePhrases, userCategories])

  // Unified filter function - searches phrase text, category (folder), tags, or cipher values
  const filterPhrase = (phrase: StoredPhrase): boolean => {
    const phraseTags = normalizeTags(phrase.tags)
    const searchTerms = searchFilter
      .split(',')
      .map((t: string) => t.trim())
      .filter((t: string) => t.length > 0)
    
    // If no search terms, show all phrases
    if (searchTerms.length === 0) return true
    
    // If searchByValue is enabled, search by cipher values
    if (searchByValue) {
      return searchTerms.some((term: string) => {
        const searchNum = parseInt(term, 10)
        if (isNaN(searchNum)) return false
        
        // Check if any visible cipher matches the search number
        return phrase.results?.some(result => {
          if (visibleCiphers.size > 0 && !visibleCiphers.has(result.name)) {
            return false
          }
          return result.value === searchNum
        }) || false
      })
    }
    
    // Normal text search - check if ANY search term matches phrase text, category, OR tags
    return searchTerms.some((term: string) => {
      const termLower = term.toLowerCase()
      const matchesPhrase = phrase.phrase.toLowerCase().includes(termLower)
      const matchesCategory = (phrase.category || 'uncategorized').toLowerCase().includes(termLower)
      const matchesTags = phraseTags.some((t: string) => t.toLowerCase().includes(termLower))
      
      return matchesPhrase || matchesCategory || matchesTags
    })
  }

  const fetchUserPhrases = async () => {
    if (!user) return
    
    try {
      const session = localStorage.getItem('session')
      if (!session) {
        setIsLoading(false)
        return
      }
      
      const response = await fetch(`${API_URL}/api/phrases`, {
        headers: {
          'Authorization': `Bearer ${session}`
        }
      })
      
      if (response.ok) {
        const phrases = await response.json()
        const transformed: StoredPhrase[] = phrases.map((p: any) => {
          // Handle tags - backend returns comma-separated string or might be null
          let processedTags: string[] = []
          if (p.tags) {
            if (typeof p.tags === 'string') {
              processedTags = p.tags.split(',').map((t: string) => t.trim()).filter((t: string) => t)
            } else if (Array.isArray(p.tags)) {
              processedTags = p.tags
            }
          }
          
          return {
            phrase_hash: p.phrase_hash,
            phrase: p.phrase,
            results: p.results || [],
            timestamp: p.id,
            category: p.category,
            tags: processedTags,
            discoveredBy: p.discovered_by,
            like_count: p.like_count || 0,
            userLiked: p.userLiked || false
          }
        })
        setProfilePhrases(transformed)
        
        // Note: Categories are now fetched separately via fetchCategories()
        // This ensures empty folders are preserved
      }
    } catch (error) {
      console.error('Failed to fetch phrases:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const removeStoredPhrase = async (timestamp: number | string) => {
    const session = localStorage.getItem('session')
    const csrfToken = localStorage.getItem('csrfToken')
    if (session && user) {
      try {
        const response = await fetch(`${API_URL}/api/phrases/${timestamp}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session}`,
            'X-CSRF-Token': csrfToken || ''
          }
        })
        if (!response.ok && response.status !== 404) {
          console.error('Failed to delete phrase from backend')
        }
      } catch (error) {
        console.error('Error deleting phrase:', error)
      }
    }
    setProfilePhrases(prev => prev.filter(p => p.timestamp !== timestamp))
  }

  const updatePhrase = async (timestamp: number | string, updates: { tags?: string[]; category?: string }) => {
    const session = localStorage.getItem('session')
    const csrfToken = localStorage.getItem('csrfToken')
    if (session && user) {
      try {
        const response = await fetch(`${API_URL}/api/phrases/${timestamp}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session}`,
            'X-CSRF-Token': csrfToken || ''
          },
          body: JSON.stringify({
            tags: updates.tags,
            category: updates.category
          })
        })
        if (!response.ok) {
          console.error('Failed to update phrase on backend')
        }
      } catch (error) {
        console.error('Error updating phrase:', error)
      }
    }
  }

  const handleLogout = async () => {
    const session = localStorage.getItem('session')
    const csrfToken = localStorage.getItem('csrfToken')
    if (session) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${session}`,
            'X-CSRF-Token': csrfToken || ''
          }
        })
      } catch (error) {
        console.error('Logout error:', error)
      }
    }
    localStorage.removeItem('session')
    localStorage.removeItem('csrfToken')
    localStorage.removeItem('user')
    navigate('/')
  }

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev)
      if (newSet.has(category)) {
        newSet.delete(category)
      } else {
        newSet.add(category)
      }
      return newSet
    })
  }

  const deleteCategory = async (category: string) => {
    const session = localStorage.getItem('session')
    const csrfToken = localStorage.getItem('csrfToken')
    
    if (session) {
      try {
        const response = await fetch(`${API_URL}/api/categories/${encodeURIComponent(category)}`, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${session}`,
            'X-CSRF-Token': csrfToken || ''
          }
        })
        
        if (response.ok) {
          const updatedCategories = userCategories.filter(c => c !== category)
          setUserCategories(updatedCategories)
          // Move phrases to uncategorized instead of removing them
          setProfilePhrases(prev => prev.map(p => 
            p.category === category ? { ...p, category: 'uncategorized' } : p
          ))
          // Also remove from expanded
          setExpandedCategories(prev => {
            const newSet = new Set(prev)
            newSet.delete(category)
            return newSet
          })
        }
      } catch (error) {
        console.error('Error deleting category:', error)
      }
    }
  }

  const createCategory = async () => {
    if (newCategoryName.trim() && !userCategories.includes(newCategoryName.trim())) {
      const newCat = newCategoryName.trim()
      const session = localStorage.getItem('session')
      const csrfToken = localStorage.getItem('csrfToken')
      
      if (session) {
        try {
          const response = await fetch(`${API_URL}/api/categories`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session}`,
              'X-CSRF-Token': csrfToken || ''
            },
            body: JSON.stringify({ name: newCat })
          })
          
          if (response.ok || response.status === 409) {
            // Category created or already exists
            setUserCategories(prev => {
              if (prev.includes(newCat)) return prev
              return [...prev, newCat]
            })
            setNewCategoryName('')
            // New folder starts collapsed by default
            // Refresh categories from server to ensure sync
            fetchCategories()
          } else {
            const errorData = await response.json().catch(() => ({}))
            console.error('Failed to create category:', response.status, errorData)
            alert(`Failed to create folder: ${errorData.error || 'Server error'}`)
          }
        } catch (error) {
          console.error('Error creating category:', error)
        }
      }
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" style={{ fontFamily: "'Special Elite', cursive" }}>
        <div className="text-center">
          <p className="text-green-600 mb-4">Please sign in to view your profile</p>
          <Link to="/" className="text-green-400 hover:text-green-300 underline">
            Return to Calculator
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Special Elite', cursive" }}>
      {/* Header */}
      <header className="border-b border-green-900 bg-black/50 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-lg font-bold text-green-400 hover:text-green-300 transition-colors">
                ← Calculator
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Link 
                to={`/u/${user.username}`}
                className="text-green-400 hover:text-green-300 transition-colors cursor-pointer"
                title="View your public profile"
              >
                Public
              </Link>
              <Link 
                to="/feed"
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                Feed
              </Link>
              <Link 
                to="/settings"
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                Settings
              </Link>
              <button 
                onClick={handleLogout}
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Discoverer Profile Error Toast */}
      {discovererError && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-red-900/90 border border-red-500 text-red-200 px-6 py-3 rounded-lg shadow-lg">
          {discovererError}
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Cipher Toggle */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-green-400">Show Values</h3>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const allCipherNames = new Set<string>()
                  profilePhrases.forEach(p => {
                    p.results?.forEach(r => allCipherNames.add(r.name))
                  })
                  setVisibleCiphers(allCipherNames)
                }}
                className="text-xs text-green-500 hover:text-green-400"
              >
                All
              </button>
              <button
                onClick={() => setVisibleCiphers(new Set())}
                className="text-xs text-green-500 hover:text-green-400"
              >
                None
              </button>
              <button
                onClick={async () => {
                  const session = localStorage.getItem('session')
                  if (!session) {
                    alert('Please sign in to save cipher selections')
                    return
                  }
                  
                  const ciphersToSave = Array.from(visibleCiphers)
                  const csrfToken = localStorage.getItem('csrfToken')
                  try {
                    const response = await fetch(`${API_URL}/api/settings/visibleCiphers`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session}`,
                        'X-CSRF-Token': csrfToken || ''
                      },
                      body: JSON.stringify({ value: JSON.stringify(ciphersToSave) })
                    })
                    
                    if (response.ok) {
                      alert(`Saved ${ciphersToSave.length} cipher selections to server`)
                    } else {
                      alert('Failed to save cipher selections')
                    }
                  } catch (error) {
                    alert('Network error. Please try again.')
                  }
                }}
                className="text-xs text-amber-500 hover:text-amber-400"
              >
                Save
              </button>
              <button
                onClick={loadSavedCipherSelection}
                className="text-xs text-amber-500 hover:text-amber-400"
              >
                Load
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {(() => {
              const availableCiphers = new Set<string>()
              profilePhrases.forEach(p => {
                p.results?.forEach(r => availableCiphers.add(r.name))
              })
              // Sort by cipherOrder, only show available ciphers
              return cipherOrder
                .filter(cipherName => availableCiphers.has(cipherName))
                .map(cipherName => {
                  const colors = cipherColors[cipherName] || { name: 'text-green-400', bg: 'bg-green-900' }
                  return (
                      <label key={cipherName} className={`flex items-center gap-1 text-xs cursor-pointer hover:bg-green-900/30 px-2 py-1 rounded border border-green-800 ${colors.name}`}>
                      <input
                        type="checkbox"
                        checked={visibleCiphers.has(cipherName)}
                        onChange={(e) => {
                          const newSet = new Set(visibleCiphers)
                          if (e.target.checked) {
                            newSet.add(cipherName)
                          } else {
                            newSet.delete(cipherName)
                          }
                          setVisibleCiphers(newSet)
                        }}
                        className="w-3 h-3 accent-green-500"
                      />
                      <span className="font-bold">{cipherDisplayNames[cipherName] || cipherName}</span>
                    </label>
                  )
                })
            })()}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              <h3 className="text-sm font-semibold text-green-400">Search</h3>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchByValue}
                  onChange={(e) => setSearchByValue(e.target.checked)}
                  className="w-4 h-4 accent-green-500"
                />
                <span className="text-green-400">Lookup by key</span>
              </label>
            </div>
          </div>
          <div className="flex gap-4">
            <input
              type="text"
              placeholder={searchByValue ? "Enter cipher value (e.g., 73, 666)" : "Search by phrase, folder, or tag..."}
              value={searchFilter}
              onChange={(e) => {
                // Only allow alphanumeric, commas, periods, and spaces
                const sanitized = e.target.value.replace(/[^a-zA-Z0-9,\.\s]/g, '')
                setSearchFilter(sanitized)
              }}
              className="flex-1 px-4 py-3 bg-black border border-green-700 rounded-lg focus:border-green-400 focus:outline-none text-green-400"
            />
            {searchFilter && (
              <button
                onClick={() => setSearchFilter('')}
                className="px-4 py-3 bg-green-900/50 border border-green-600 text-green-400 rounded-lg hover:bg-green-900/70"
              >
                Clear ×
              </button>
            )}
          </div>
        </div>

        <div 
          ref={mainContainerRef}
          className={`grid grid-cols-1 lg:grid-cols-4 gap-6 ${isSelecting ? 'select-none' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Sidebar - Categories as Folders */}
          <div className="lg:col-span-1">
            <div className="bg-green-950/20 border border-green-800 rounded-lg p-4 sticky top-24">
              <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center gap-2">
                <span>📁</span> Folders
              </h3>
              
              {/* Create New Category */}
              <div className="space-y-2 mb-4">
                <input
                  type="text"
                  placeholder="New folder..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') createCategory()
                  }}
                  className="w-full px-3 py-2 bg-black border border-green-700 rounded-lg focus:border-green-400 focus:outline-none text-green-400 text-sm"
                />
                <button
                  onClick={createCategory}
                  disabled={!newCategoryName.trim() || userCategories.includes(newCategoryName.trim())}
                  className="w-full px-3 py-2 bg-green-700 hover:bg-green-600 disabled:bg-green-900 disabled:text-green-700 text-black font-semibold rounded-lg transition-colors text-sm"
                >
                  Create Folder
                </button>
              </div>

              {/* Category List - Collapsible */}
              <div className="space-y-1">
                {userCategories.filter(c => c !== 'all').map(category => {
                  const phrasesInCategory = phrasesByCategory[category] || []
                  const filteredCount = phrasesInCategory.filter(filterPhrase).length
                  const isExpanded = expandedCategories.has(category)
                  
                  return (
                    <FolderItem
                      key={category}
                      category={category}
                      phrasesInCategory={phrasesInCategory}
                      filteredCount={filteredCount}
                      isExpanded={isExpanded}
                      isDraggingPhrases={isDraggingPhrases}
                      activeDropTarget={activeDropTarget}
                      setActiveDropTarget={setActiveDropTarget}
                      toggleCategory={toggleCategory}
                      setFolderToDelete={setFolderToDelete}
                      searchFilter={searchFilter}
                      filterPhrase={filterPhrase}
                      setEditingPhrase={setEditingPhrase}
                      setEditTags={setEditTags}
                      setEditCategory={setEditCategory}
                      normalizeTags={normalizeTags}
                    />
                  )
                })}
              </div>
            </div>
          </div>

          {/* Main Content - All Phrases Grid */}
          <div className="lg:col-span-3">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={() => setIsDraggingPhrases(true)}
              onDragEnd={handleDragEnd}
            >
            {/* Edit Dialog */}
            {editingPhrase && (
              <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-black rounded-2xl p-6 w-full max-w-md border border-green-500 shadow-2xl">
                  <h3 className="text-xl font-bold text-green-400 mb-4">Edit Phrase</h3>
                  <p className="text-green-300 mb-4 text-sm">{editingPhrase.phrase}</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-green-500 mb-2">Tags (comma-separated)</label>
                       <input
                         type="text"
                         value={editTags}
                         onChange={(e) => {
                           // Only allow alphanumeric, commas, spaces, and hyphens
                           const sanitized = e.target.value.replace(/[^a-zA-Z0-9,\s-]/g, '')
                           setEditTags(sanitized)
                         }}
                         placeholder="tag1, tag2, tag3"
                         className="w-full px-3 py-2 bg-black border border-green-600 rounded-lg focus:border-green-400 focus:outline-none text-green-400 text-sm"
                       />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-green-500 mb-2">Folder</label>
                      <select
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        className="w-full px-3 py-2 bg-black border border-green-600 rounded-lg focus:border-green-400 focus:outline-none text-green-400 text-sm"
                      >
                        {userCategories.filter(c => c !== 'all').map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-6">
                    <button
                      onClick={() => setEditingPhrase(null)}
                      className="flex-1 px-4 py-2 text-green-500 hover:text-green-300 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                     <button
                       onClick={() => {
                         // Sanitize tags to only allow alphanumeric, spaces, and hyphens
                         const tags = editTags.split(',').map(t => t.trim().replace(/[^a-zA-Z0-9\s-]/g, '')).filter(t => t.length > 0)
           setProfilePhrases(prev => prev.map(p => 
             p.timestamp === editingPhrase.timestamp 
               ? { ...p, tags, category: editCategory }
               : p
           ))
                         updatePhrase(editingPhrase.timestamp, {
                           tags,
                           category: editCategory
                         })
                         setEditingPhrase(null)
                       }}
                       className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-black font-semibold rounded-lg transition-colors text-sm"
                     >
                       Save Changes
                     </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Folder Confirmation */}
            {folderToDelete && (
              <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-black rounded-2xl p-6 w-full max-w-md border border-red-500 shadow-2xl">
                  <h3 className="text-xl font-bold text-red-400 mb-4">Delete Folder</h3>
                  <p className="text-green-300 mb-2 text-sm">
                    Are you sure you want to delete the folder "{folderToDelete}"?
                  </p>
                  <p className="text-green-600 text-xs mb-4">
                    Phrases in this folder will be moved to "uncategorized".
                  </p>
                  
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setFolderToDelete(null)}
                      className="flex-1 px-4 py-2 text-green-500 hover:text-green-300 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        deleteCategory(folderToDelete)
                        setFolderToDelete(null)
                      }}
                      className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-black font-semibold rounded-lg transition-colors text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Phrases Display */}
            <div>
              <h3 className="text-lg font-semibold text-green-400 mb-4 flex items-center justify-between">
                <span>{profilePhrases.filter(filterPhrase).length} Matching Phrases</span>
                <div className="flex gap-2">
                  <ExportButton phrases={profilePhrases.filter(filterPhrase)} />
                  <ImportButton onImport={() => fetchUserPhrases()} />
                </div>
              </h3>

              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="border border-green-800 rounded-lg p-4 bg-green-950/10 animate-pulse">
                      <div className="h-4 bg-green-900/30 rounded w-3/4 mb-3"></div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {[1, 2, 3].map(j => (
                          <div key={j} className="h-4 bg-green-900/30 rounded w-16"></div>
                        ))}
                      </div>
                      <div className="flex gap-2 mb-2">
                        <div className="h-5 bg-green-900/30 rounded w-20"></div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <div className="h-3 bg-green-900/30 rounded w-10"></div>
                        <div className="h-3 bg-green-900/30 rounded w-12"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : profilePhrases.filter(filterPhrase).length === 0 ? (
                <div className="text-center py-12 border border-green-800 rounded-lg">
                  <p className="text-green-600">
                    {searchFilter ? 'No phrases match your search' : 'No phrases stored yet'}
                  </p>
                  <Link to="/" className="text-green-400 hover:text-green-300 underline mt-2 inline-block">
                    Add phrases from the calculator
                  </Link>
                </div>
              ) : (
                <div 
                  ref={phrasesGridRef}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 relative"
                >
                  {/* Selection Box Overlay */}
                  {selectionBox && (
                    <div
                      className="absolute border-2 border-green-400 bg-green-400/20 pointer-events-none z-10"
                      style={{
                        left: Math.min(selectionBox.startX, selectionBox.currentX),
                        top: Math.min(selectionBox.startY, selectionBox.currentY),
                        width: Math.abs(selectionBox.currentX - selectionBox.startX),
                        height: Math.abs(selectionBox.currentY - selectionBox.startY),
                      }}
                    />
                  )}
                  {profilePhrases
                    .filter(filterPhrase)
                    .map((phrase) => {
                      const isSelected = selectedPhrases.has(String(phrase.timestamp))
                      return (
                        <div 
                          key={phrase.timestamp} 
                          data-phrase-id={phrase.timestamp}
                          className={`border rounded-lg p-4 transition-colors bg-green-950/10 flex flex-col cursor-pointer ${
                            isSelected 
                              ? 'border-green-400 ring-2 ring-green-400 bg-green-400/20' 
                              : 'border-green-800 hover:border-green-600'
                          }`}
                          onClick={(_e) => {
                            // Toggle selection on click if not starting drag
                            if (!isSelecting) {
                              const newSelection = new Set(selectedPhrases)
                              if (newSelection.has(String(phrase.timestamp))) {
                                newSelection.delete(String(phrase.timestamp))
                              } else {
                                newSelection.add(String(phrase.timestamp))
                              }
                              setSelectedPhrases(newSelection)
                            }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, phrase)}
                        >
                          <div className="flex items-start justify-center mb-2 gap-4">
                            <p className={`text-center ${highlightedPhrases.some(hp => hp.toLowerCase() === phrase.phrase.toLowerCase()) ? 'text-amber-400 font-bold' : 'text-green-300 font-medium'}`}>{phrase.phrase}</p>
                          </div>
                          
                          {/* Gematria Values */}
                          {phrase.results && phrase.results.length > 0 && (
                            <div className="flex flex-wrap justify-center gap-3 mb-3 text-xs">
                              {(() => {
                                // Get search values if in lookup mode
                                const searchValues = searchByValue 
                                  ? searchFilter.split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n))
                                  : []
                                
                                // Recalculate results to include numeric digit support
                                const calculatedResults = calculateAll(phrase.phrase)
                                
                                // Filter results to show
                                let resultsToShow = calculatedResults
                                
                                if (searchByValue && searchValues.length > 0) {
                                  // In lookup mode: only show matching values
                                  resultsToShow = calculatedResults.filter(result => 
                                    searchValues.includes(result.value)
                                  )
                                } else if (visibleCiphers.size > 0) {
                                  // Normal mode: show visible ciphers
                                  resultsToShow = calculatedResults.filter(result => 
                                    visibleCiphers.has(result.name)
                                  )
                                } else {
                                  // Nothing to show
                                  return null
                                }
                                
                                if (resultsToShow.length === 0) return null
                                
                                return resultsToShow.map((result, idx) => {
                                  const colors = cipherColors[result.name] || { name: 'text-green-600', value: 'text-green-400' }
                                  const displayName = cipherDisplayNames[result.name] || result.name
                                  const isHighlighted = shouldHighlightValue(result.value)
                                  return (
                                    <span key={idx}>
                                      <span className={colors.name}>{displayName}:</span>{' '}
                                      <span 
                                        className={`font-mono font-semibold ${isHighlighted ? 'text-amber-400' : colors.value}`}
                                        style={isHighlighted ? { textShadow: '0 0 8px rgba(251, 191, 36, 0.8), 0 0 16px rgba(251, 191, 36, 0.4)' } : undefined}
                                      >
                                        {result.value}
                                      </span>
                                    </span>
                                  )
                                })
                              })()}
                            </div>
                          )}
                          
                          {/* Category & Tags */}
                          <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
                            {phrase.category && phrase.category !== 'uncategorized' && (
                              <span className="px-2 py-1 bg-green-900/50 text-green-400 rounded">
                                📁 {phrase.category}
                              </span>
                            )}
                            {normalizeTags(phrase.tags).map((tag: string, i: number) => (
                              <span
                                key={i}
                                className="px-2 py-1 bg-green-800/50 text-green-300 rounded"
                              >
                                #{tag}
                              </span>
                            ))}
                          </div>
                          
                          {/* Bottom Row - Discovered by (left) and Edit/Delete (right) */}
                          <div className="flex items-center justify-between mt-auto pt-2">
                            <div className="flex items-center gap-3">
                              {phrase.discoveredBy && (
                                <span 
                                  className="text-green-600 text-xs"
                                  title={`First discovered by ${phrase.discoveredBy}`}
                                >
                                  Discovered by: <button 
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      checkDiscovererProfile(phrase.discoveredBy!)
                                    }}
                                    className="text-green-500 hover:text-green-400 cursor-pointer"
                                    style={{ background: 'none', border: 'none', padding: 0 }}
                                  >{phrase.discoveredBy}</button>
                                </span>
                              )}
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleLike(phrase.phrase_hash)
                                  }}
                                  className={`text-sm transition-colors ${phrase.userLiked ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}`}
                                >
                                  {phrase.userLiked ? '❤' : '♡'}
                                </button>
                                <span className="text-xs text-gray-400">{phrase.like_count}</span>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-auto">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setEditingPhrase(phrase)
                                  const tags = normalizeTags(phrase.tags)
                                  setEditTags(tags.join(', '))
                                  setEditCategory(phrase.category || 'uncategorized')
                                }}
                                className="text-amber-400 hover:text-amber-300 text-[10px]"
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  removeStoredPhrase(phrase.timestamp)
                                }}
                                className="text-red-500 hover:text-red-400 text-[10px]"
                              >
                                Delete
                              </button>
                             </div>
                           </div>
                          </div>
                      )
                    })}
                </div>
              )}

               {/* Context Menu */}
               {contextMenu.visible && selectedPhrases.size > 0 && (
                 <div 
                   data-context-menu
                   className="fixed bg-black border border-green-500 rounded-lg shadow-2xl z-50 py-2 min-w-[200px]"
                   style={{ left: contextMenu.x, top: contextMenu.y }}
                   onClick={(e) => e.stopPropagation()}
                 >
                  <div className="px-4 py-2 text-green-400 text-sm border-b border-green-800">
                    {selectedPhrases.size} phrase{selectedPhrases.size > 1 ? 's' : ''} selected
                  </div>
                  
                  {/* Add to Folder Option */}
                  <div className="p-2 relative" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setFolderDropdownOpen(!folderDropdownOpen)
                      }}
                      className="w-full px-3 py-2 bg-black border border-green-600 rounded text-green-400 text-sm focus:border-green-400 focus:outline-none text-left flex items-center justify-between"
                    >
                      <span className={targetFolder ? 'text-green-300' : 'text-green-600'}>
                        {targetFolder || 'Select folder...'}
                      </span>
                      <span className="text-green-500 ml-2">{folderDropdownOpen ? '▲' : '▼'}</span>
                    </button>
                    
                    {folderDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-black border border-green-600 rounded shadow-xl z-60 max-h-48 overflow-y-auto">
                        {userCategories.filter(c => c !== 'all').map(cat => (
                          <button
                            key={cat}
                            onClick={(e) => {
                              e.stopPropagation()
                              setTargetFolder(cat)
                              setFolderDropdownOpen(false)
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-green-900/50 transition-colors ${
                              cat === targetFolder ? 'text-green-300' : 'text-green-400'
                            }`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {targetFolder && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          addSelectedToFolder()
                        }}
                        className="w-full mt-2 px-3 py-2 bg-green-700 hover:bg-green-600 text-black font-semibold rounded text-sm transition-colors"
                      >
                        Add to folder
                      </button>
                    )}
                  </div>
                  
               {/* Delete All Option */}
               <button
                 onClick={() => {
                   setShowDeleteConfirm(true)
                   setContextMenu(prev => ({ ...prev, visible: false }))
                 }}
                 className="w-full px-4 py-2 text-left text-red-400 hover:bg-red-950/50 transition-colors text-sm"
               >
                 Delete all
               </button>
             </div>
           )}

           {/* Delete Confirmation Dialog */}
           {showDeleteConfirm && (
             <div data-delete-confirm className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
               <div className="bg-black rounded-2xl p-6 w-full max-w-md border border-red-500 shadow-2xl">
                 <h3 className="text-xl font-bold text-red-400 mb-4">Delete Phrases</h3>
                 <p className="text-green-300 mb-4 text-sm">
                   Are you sure you want to delete {selectedPhrases.size} phrase{selectedPhrases.size > 1 ? 's' : ''}?
                 </p>
                 <p className="text-green-600 text-xs mb-6">
                   This action cannot be undone.
                 </p>
                 
                 <div className="flex gap-3">
                   <button
                     onClick={() => setShowDeleteConfirm(false)}
                     className="flex-1 px-4 py-2 text-green-500 hover:text-green-300 transition-colors text-sm"
                   >
                     Cancel
                   </button>
                   <button
                     onClick={bulkDeletePhrases}
                     className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-black font-semibold rounded-lg transition-colors text-sm"
                   >
                     Delete
                   </button>
                 </div>
               </div>
             </div>
           )}
         </div>
         </DndContext>
          </div>
        </div>
      </div>
    </div>
  )
}
